import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Chip, Divider, Button } from "@material-ui/core";
import { Email, LinkedIn, Twitter, GetApp, GitHub } from "@material-ui/icons";
import Navbar from "./Navbar";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#121212",
    color: "#fff",
    minHeight: "100vh",
    paddingBottom: "4rem",
  },

  section: {
    maxWidth: "900px",
    margin: "auto",
    padding: "2rem",
  },

  heading: {
    color: "#00fff5",
    marginBottom: "1rem",
    fontFamily: "Quicksand",
    fontWeight: 700,
    letterSpacing: "2px",
  },

  subHeading: {
    color: "#00fff5",
    fontFamily: "Quicksand",
    marginBottom: "0.3rem",
    marginTop: "1rem",
  },

  chip: {
    margin: "4px",
    background: "#1f1f1f",
    border: "1px solid #00fff5",
    color: "#00fff5",
    fontWeight: 600,
  },

  card: {
    padding: "1.5rem",
    margin: "1rem 0",
    background: "rgba(243, 236, 236, 0.93)",
    backdropFilter: "blur(4px)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  },

  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "6px",
  },

  downloadBtn: {
    marginTop: "1rem",
    background: "#00fff5",
    color: "#000",
    fontWeight: "700",
    "&:hover": {
      background: "#16c7ba",
    },
  },
}));

const Resume = () => {
  const classes = useStyles();

  return (
    <>
      <Navbar />

      <Box className={classes.root}>
        {/* ===================== Header ===================== */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.heading}>
            PUKAR UPRETI
          </Typography>
          <Typography variant="h6" style={{ opacity: 0.9 }}>
            Software Engineer • Web Developer • IT Service Desk Officer
          </Typography>

          <Divider style={{ background: "#00fff5", margin: "1rem 0" }} />

          {/* Contact */}

          <div
            style={{
              display: "flex",
              gap: "20px",
              marginTop: "1rem",
            }}
          >
            {/* Email */}
            <a
              href="https://mail.google.com/mail/?view=cm&to=pukarupreti3@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#00fff5", fontSize: "28px" }}
            >
              <Email fontSize="inherit" />
            </a>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/in/pukar-upreti/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#0A66C2", fontSize: "28px" }}
            >
              <LinkedIn fontSize="inherit" />
            </a>

            {/* Twitter */}
            <a
              href="https://x.com/___pukar"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1DA1F2", fontSize: "28px" }}
            >
              <Twitter fontSize="inherit" />
            </a>

            {/* GitHub */}
            <a
              href="https://github.com/PukarHub"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#fff", fontSize: "28px" }}
            >
              <GitHub fontSize="inherit" />
            </a>
          </div>

          {/* Download Button */}
          <Button
            variant="contained"
            startIcon={<GetApp />}
            className={classes.downloadBtn}
            download
          >
            <a
              href="/Pdf/Pukar Resume.pdf"
              download
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Download Resume
            </a>
          </Button>
        </Box>

        {/* ===================== Summary ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Professional Summary
          </Typography>
          <Typography>
            Motivated Software Engineer currently working in IT Support, seeking
            to transition into a full-time role in software engineering or web
            development. Experienced in building full-stack applications using
            React, Node.js, and modern JavaScript technologies.
          </Typography>
        </Box>

        {/* ===================== Skills ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Technical Skills
          </Typography>

          <Typography className={classes.subHeading}>Languages</Typography>
          <Chip label="JavaScript" className={classes.chip} />
          <Chip label="TypeScript" className={classes.chip} />
          <Chip label="HTML5" className={classes.chip} />
          <Chip label="CSS3" className={classes.chip} />

          <Typography className={classes.subHeading}>Frameworks</Typography>
          <Chip label="ReactJS" className={classes.chip} />
          <Chip label="NodeJS" className={classes.chip} />
          <Chip label="ExpressJS" className={classes.chip} />
          <Chip label="AngularJS" className={classes.chip} />
          <Chip label="Redux" className={classes.chip} />

          <Typography className={classes.subHeading}>Databases</Typography>
          <Chip label="MongoDB" className={classes.chip} />
          <Chip label="PostgreSQL" className={classes.chip} />

          <Typography className={classes.subHeading}>Tools</Typography>
          <Chip label="Git" className={classes.chip} />
          <Chip label="Jira" className={classes.chip} />
          <Chip label="AWS" className={classes.chip} />
          <Chip label="ServiceNow" className={classes.chip} />

          <Typography className={classes.subHeading}>
            IT & Support Skills
          </Typography>
          <Chip
            label="Windows & macOS troubleshooting"
            className={classes.chip}
          />
          <Chip label="Active Directory" className={classes.chip} />
          <Chip label="Microsoft 365" className={classes.chip} />
          <Chip label="Hardware support" className={classes.chip} />
          <Chip
            label="Networking basics (TCP/IP, VPN, DNS)"
            className={classes.chip}
          />
          <Chip label="Ticketing systems" className={classes.chip} />
          <Chip label="Technical Documentation" className={classes.chip} />
        </Box>

        {/* ===================== Experience ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Experience
          </Typography>
          <Typography>
            Technology Service Desk Officer — Bunnings Support Office WA (Feb
            2025–Present)
            <ul>
              <li>Provided first-level IT support and managed incidents.</li>
              <li>Handled onboarding/offboarding and Microsoft 365 setup.</li>
              <li>Created SOPs and knowledge base documentation.</li>
            </ul>
          </Typography>
          <Typography>
            Customer Support and Service | Bunnings Warehouse WA (Aug 2023 – Feb
            2025)
            <ul>
              <li>
                Front-end support at store checkout, ensuring excellent customer
                service.
              </li>
              <li>
                Managing customer queries related to refunds, returns, and hire
                services.
              </li>
              <li>
                Collaborating with the store team to resolve any issues
                efficiently.
              </li>
            </ul>
          </Typography>
          <Typography>
            Software Engineer — Four Leaf Digital (2023–2025)
            <ul>
              <li>Built MERN stack applications for client projects.</li>
              <li>Created responsive interfaces in React, Vue & WordPress.</li>
              <li>Automated deployments with AWS, Netlify & Firebase.</li>
            </ul>
          </Typography>

          <Typography>
            Software Engineer — Deerhold Nepal (2021–2023)
            <ul>
              <li>Developed full-stack applications for US healthcare.</li>
              <li>Built secure authentication using JWT & Passport.</li>
              <li>Worked with distributed teams across Japan & the US.</li>
            </ul>
          </Typography>
        </Box>

        {/* ===================== Education ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Education
          </Typography>

          <Typography variant="h6">
            Bachelor of Computer Science & Information Technology
          </Typography>
          <Typography>
            Tribhuvan University | Kathamdnu Nepal — 2017–2021
          </Typography>
        </Box>

        {/* ===================== Certifications ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Certifications
          </Typography>

          <ul>
            <li>JavaScript Foundations — Mozilla (2024)</li>
            <li>React Essential Training — LinkedIn (2024)</li>
            <li>Java Data Structure — LinkedIn (2024)</li>
            <li>IT Service Desk Careers — LinkedIn (2024)</li>
            <li>HIPAA Compliance Certified — Deerhold Nepal</li>
          </ul>
        </Box>

        {/* ===================== Projects ===================== */}
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.subHeading}>
            Projects
          </Typography>

          <ul>
            <li>Winner — React Workshop, Deerwalk Developer Community</li>
            <li>
              Open-source contributions on GitHub (Pull Shark, Arctic Vault)
            </li>
          </ul>
        </Box>
      </Box>
    </>
  );
};

export default Resume;
